import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChargerSidebar } from '../../components/charger_planning/ChargerSidebar';
import { ChargerGoogleMap } from '../../components/charger_planning/ChargerGoogleMap';
import { LandParcelSuggestionPanel } from '../../components/charger_planning/LandParcelSuggestionPanel';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/chargerApiConfig';

function ChargerMapView() {
    const [collapsed, setCollapsed] = useState(false);
    const [ocParcelData, setOcParcelData] = useState([]);
    const [ocParcelLoading, setOcParcelLoading] = useState(false);
    const [scaqmdJurisdiction, setScaqmdJurisdiction] = useState([]);
    const [scaqmdJurisdictionLoading, setScaqmdJurisdictionLoading] = useState(false);
    const [mhdInfrastructure, setMhdInfrastructure] = useState([]);
    const [mhdInfrastructureLoading, setMhdInfrastructureLoading] = useState(false);
    const [warehouses, setWarehouses] = useState([]);
    const [warehousesLoading, setWarehousesLoading] = useState(false);
    const [truckStopData, setTruckStopData] = useState([]);
    const [truckStopDataLoading, setTruckStopDataLoading] = useState(false);

    const [needManualSearch, setNeedManualSearch] = useState(false);

    const mapRef = useRef(null);
    const lastBoundsRef = useRef(null);
    const loadingTimeoutRef = useRef(null);

    // Map state for viewport-based loading
    const [mapBounds, setMapBounds] = useState(null);
    const [zoomLevel, setZoomLevel] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    // Search/Filter state
    const [searchFilters, setSearchFilters] = useState({
        zipCode: '',
        city: '',
        minLandValue: '',
        maxLandValue: '',
        minArea: '',
        maxArea: ''
    });
    const [isFiltering, setIsFiltering] = useState(false);

    // Debounced viewport fetching function
    const debouncedFetchMapData = useCallback((bounds, zoom, filters = {}) => {
        if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
        }

        loadingTimeoutRef.current = setTimeout(() => {
            fetchMapViewportData(bounds, zoom, filters);
        }, 1000); // 1000ms debounce
    }, []);

    // Fetch data for current map viewport
    const fetchMapViewportData = useCallback(async (bounds, zoom, filters = {}) => {
        if (!bounds) return;

        try {
            setOcParcelLoading(true);

            const params = {
                bounds: `${bounds.south},${bounds.west},${bounds.north},${bounds.east}`,
                zoom: zoom,
                page: 1,
                ...filters
            };

            // Remove empty filter values
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) {
                    delete params[key];
                }
            });

            const response = await axios.get(API_ENDPOINTS.getOCParcelData, {
                params: params,
                headers: {
                    Authorization: `Token ${localStorage.getItem('authToken')}`
                }
            });

            const { results, count } = response.data;

            setTotalCount(count);
            setOcParcelData(results);

            lastBoundsRef.current = bounds;

        } catch (error) {
            console.error('Error fetching viewport parcel data:', error);
        } finally {
            setOcParcelLoading(false);
        }
    }, []);

    const getSCAQMDJurisdiction = useCallback(async () => {
        setScaqmdJurisdictionLoading(true);
        const response = await axios.get(API_ENDPOINTS.getSCAQMDJurisdiction, {
            headers: {
                Authorization: `Token ${localStorage.getItem('authToken')}`
            }
        })
            .catch(error => {
                console.error('Error fetching SCAQMD Jurisdiction:', error);
            });

        setScaqmdJurisdiction(response.data);
        console.log("SCAQMD Jurisdiction", response.data);
        setScaqmdJurisdictionLoading(false);
    }, []);

    useEffect(() => {
        getSCAQMDJurisdiction();
    }, []);


    const getMhdInfrastructure = useCallback(async () => {
        setMhdInfrastructureLoading(true);
        const response = await axios.get(API_ENDPOINTS.getMdhdInfrastructure, {
            headers: {
                Authorization: `Token ${localStorage.getItem('authToken')}`
            }
        });
        setMhdInfrastructure(response.data);
        console.log("MHD Infrastructure", response.data);
        setMhdInfrastructureLoading(false);
    }, []);

    useEffect(() => {
        getMhdInfrastructure();
    }, []);

    const getWarehouses = useCallback(async () => {
        setWarehousesLoading(true);
        const response = await axios.get(API_ENDPOINTS.getWarehouses, {
            headers: {
                Authorization: `Token ${localStorage.getItem('authToken')}`
            }
        });
        setWarehouses(response.data);
        console.log("Warehouses", response.data);
        setWarehousesLoading(false);
    }, []);

    useEffect(() => {
        getWarehouses();
    }, []);

    const getTruckStopData = async () => {
        setTruckStopDataLoading(true);
        const response = await fetch("/static/heatmap_js_format.json")
        console.log("Response", response);
        const data = await response.json();
        console.log("Data", data);
        setTruckStopData(data);
        console.log("Truck Stop Data", data);
        if (!data) {
            console.error('Error fetching truck stop data');
        }
        setTruckStopDataLoading(false);
        console.log(truckStopData[0])
    }

    useEffect(() => {
        console.log("Fetching truck stop data");
        getTruckStopData();
    }, []);
    
    // Handle map bounds change
    const handleMapBoundsChange = useCallback((bounds, zoom) => {
        setMapBounds(bounds);
        setZoomLevel(zoom);
        setNeedManualSearch(true);
    }, [searchFilters]);

    // Handle search/filter changes
    const handleSearch = useCallback((newFilters) => {
        setIsFiltering(true);
        setSearchFilters(newFilters);

        if (mapBounds) {
            debouncedFetchMapData(mapBounds, zoomLevel, newFilters);
        }

        setTimeout(() => setIsFiltering(false), 500);
    }, [mapBounds, zoomLevel, debouncedFetchMapData]);

    const handleSearchInArea = useCallback(async ({bounds, zoom, filters}) => {
        setNeedManualSearch(false);
        setOcParcelLoading(true);
        try {
            const response = await axios.get(API_ENDPOINTS.getOCParcelData, {
                params: {
                    bounds: `${bounds.south},${bounds.west},${bounds.north},${bounds.east}`,
                    zoom: zoom,
                    page: 1,
                    ...filters
                },
                headers: {
                    Authorization: `Token ${localStorage.getItem('authToken')}`
                }
            });
            const { results, count } = response.data;
            setOcParcelData(results);
            setTotalCount(count);
            setOcParcelLoading(false);
        } catch (error) {
            console.error('Error fetching viewport parcel data:', error);
            setOcParcelLoading(false);
        }
    }, []);

    const toggleCollapse = () => {
        setCollapsed(!collapsed);
    };

    return (
        <div className="flex h-screen bg-gray-50 font-nunito">
            <ChargerSidebar collapsed={collapsed} toggleCollapse={toggleCollapse} />
            <main className={`flex-1 bg-gray-50 ${collapsed ? "ml-[80px]" : "ml-[270px]"}`}>
                <div className="pt-4 px-4 h-full">
                    <div className="flex gap-4 h-full">
                        <div className="flex-1 h-full">
                            <ChargerGoogleMap
                                ref={mapRef}
                                landParcelData={ocParcelData}
                                onBoundsChange={handleMapBoundsChange}
                                scaqmdJurisdiction={scaqmdJurisdiction}
                                mhdInfrastructure={mhdInfrastructure}
                                warehouses={warehouses}
                                needManualSearch={needManualSearch}
                                handleSearchInArea={() => handleSearchInArea({bounds: mapBounds, zoom: zoomLevel, filters: searchFilters})}
                            />
                        </div>
                        <div className="flex-1 h-full">
                            <LandParcelSuggestionPanel
                                parcels={ocParcelData}
                                loading={ocParcelLoading}
                                totalCount={totalCount}
                                onSearch={handleSearch}
                                searchFilters={searchFilters}
                                isFiltering={isFiltering}
                            />
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-4 right-4 space-y-2">
                    {ocParcelLoading && (
                        <div className="bg-white text-gray-600 px-3 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-600 border-t-transparent"></div>
                            Loading Land Parcel Data...
                        </div>
                    )}
                    {scaqmdJurisdictionLoading && (
                        <div className="bg-white text-gray-600 px-3 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-600 border-t-transparent"></div>
                            Loading SCAQMD Jurisdiction Data...
                        </div>
                    )}
                    {mhdInfrastructureLoading && (
                        <div className="bg-white text-gray-600 px-3 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-600 border-t-transparent"></div>
                            Loading MHD Infrastructure Data...
                        </div>
                    )}
                    {warehousesLoading && (
                        <div className="bg-white text-gray-600 px-3 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-600 border-t-transparent"></div>
                            Loading Warehouse Data...
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}

export default ChargerMapView;